import {
  RESERVED_COLLECTION_IDS,
  basePlaylistId,
  infinityIdOfView,
  templateForViewId,
  viewIdKind,
  wallIdOfView,
  type SourceConfig,
} from "@gooner/shared";

/*
 * WHAT THE VIEW AND REGION EVENTS MAY SAY ABOUT WHAT IS PLAYING (2026-10-21).
 *
 * `view_played` / `view_watched` and `region_source_set` used to carry a `name` or a
 * `collection` title, decided by the SHAPE of the view (every region a playlist meant
 * "collection", and a collection's name went out). Shape is not ownership: a custom view
 * made only of collections, or a temp view minted from one of your own collections, sent
 * the user's own words. The rule now is decided by KIND or SOURCE, never by the name:
 *
 *  - Built-in things (the quick setups, the walls, the scrolls and games, Editor's Choice)
 *    are the site's own words. They keep `name` byte-for-byte (saved insights filter on
 *    it) and add a stable key: the definition's own view id for views, the reserved id or
 *    the Editor's Choice playlist id for collections.
 *  - Anything a user made sends no name and no title, only a PSEUDONYMOUS id. Not the raw
 *    id: a temp view's id is a write claim, a room's id is its invite link, and a private
 *    collection's id is what plays it — none of those may reach a third party. The hash is
 *    stable, so "how many distinct views" and "the same view again" still work, and anyone
 *    holding the database can recompute it for a row (analyticsId below).
 *
 * Pure (no window, no network) so tools/checks/analytics-privacy-ids.mts can import it.
 */

/**
 * A stable, one-way stand-in for an id: FNV-1a 64 over the UTF-8 bytes, 16 hex digits.
 * Reproduce for a row with the same three lines anywhere (offset 0xcbf29ce484222325,
 * prime 0x100000001b3, xor then multiply, mod 2^64).
 */
export function analyticsId(id: string): string {
  const MASK = (BigInt(1) << BigInt(64)) - BigInt(1);
  const PRIME = BigInt("0x100000001b3");
  let h = BigInt("0xcbf29ce484222325");
  for (const byte of new TextEncoder().encode(id)) {
    h ^= BigInt(byte);
    h = (h * PRIME) & MASK;
  }
  return h.toString(16).padStart(16, "0");
}

export type ViewPlayKind =
  | "premium_wall"
  | "community_wall"
  | "infinity_walls"
  | "quick_setup"
  | "collection"
  | "view";

/** The default name a collection view is minted with (lib/playCollection.ts). */
const MINTED_COLLECTION_NAME = "Collection";

export interface ViewPlayInput {
  name: string;
  regions: ReadonlyArray<{ source: SourceConfig }>;
}

/** The Editor's Choice catalog by playlist id → title (the admin's words, not a user's). */
export type EditorsChoiceIndex = ReadonlyMap<string, string>;

export interface ViewPlayProps {
  kind: ViewPlayKind;
  /** Built-ins only: the site's own name for it, unchanged. */
  name?: string;
  /** Built-ins only: a stable key that survives a rename. */
  view_key?: string;
  /** Everything a user made: a pseudonymous id, never the id itself. */
  view_id?: string;
}

/** The same KIND the events always reported — a closed enum, unchanged. */
export function viewPlayKind(viewId: string, v: ViewPlayInput): ViewPlayKind {
  const k = viewIdKind(viewId);
  if (k === "wall") return wallIdOfView(viewId) === "premium" ? "premium_wall" : "community_wall";
  if (k === "infinity") return "infinity_walls";
  if (k === "builtin") return "quick_setup";
  return v.regions.length > 0 && v.regions.every((r) => r.source.kind === "playlist") ? "collection" : "view";
}

/**
 * The one Editor's Choice collection a view plays, if that is ALL it is: a temporary view
 * (what a collection tap or /play/collection mints) whose every region plays the same
 * playlist. A saved view is always somebody's own, so it never qualifies.
 */
export function editorsChoiceSourceOf(viewId: string, v: ViewPlayInput): string | null {
  if (viewIdKind(viewId) !== "temp" || v.regions.length === 0) return null;
  const ids = new Set(v.regions.map((r) => (r.source.kind === "playlist" ? r.source.playlistId : null)));
  if (ids.size !== 1) return null;
  const [only] = [...ids];
  return only && !RESERVED_COLLECTION_IDS.has(basePlaylistId(only)) ? only : null;
}

/**
 * What `view_played` / `view_watched` say about WHICH view (besides framing and timing).
 * `editorsChoice` is the Editor's Choice catalog, or null while it is unknown — unknown
 * means "not provably built-in", so the view reports as a user's.
 */
export function viewPlayProps(
  viewId: string,
  v: ViewPlayInput,
  editorsChoice: EditorsChoiceIndex | null,
): ViewPlayProps {
  const kind = viewPlayKind(viewId, v);
  switch (viewIdKind(viewId)) {
    case "wall":
      if (wallIdOfView(viewId)) return { kind, name: v.name, view_key: viewId };
      break;
    case "infinity":
      if (infinityIdOfView(viewId)) return { kind, name: v.name, view_key: viewId };
      break;
    case "builtin": {
      const t = templateForViewId(viewId);
      if (t) return { kind, name: t.name, view_key: viewId };
      break;
    }
    default: {
      const ec = editorsChoiceSourceOf(viewId, v);
      const title = ec ? editorsChoice?.get(ec) : undefined;
      if (ec && title !== undefined) {
        // The minted default stays as it always was; anything else is the catalog's own
        // title, so a temp view somebody renamed still never sends what they typed.
        return {
          kind,
          name: v.name === MINTED_COLLECTION_NAME ? MINTED_COLLECTION_NAME : title,
          view_key: `collection:${ec}`,
        };
      }
    }
  }
  return { kind, view_id: analyticsId(viewId) };
}

/** A collection option as the pickers hold it (control/lib/feedData.ts). */
export interface CollectionRef {
  id: string;
  editorsChoice?: boolean;
  own?: boolean;
}

/**
 * The collection half of `region_source_set`. Built-in collections send a stable slug:
 * the reserved virtual ones ("faps", "uploads" — owner-scoped `<id>:<uid>` is cut back to
 * the reserved id) and Editor's Choice, whose slug is its playlist id (site-owned rows,
 * `editors_choice = 1`, no title). Every other collection — public, someone else's, your
 * own — sends only a pseudonymous id. No title ever goes out.
 */
export function regionSourceCollectionProps(
  s: SourceConfig,
  collections: ReadonlyArray<CollectionRef>,
): { collection_slug: string } | { collection_id: string } | Record<string, never> {
  if (s.kind !== "playlist") return {};
  const base = basePlaylistId(s.playlistId);
  if (RESERVED_COLLECTION_IDS.has(base)) return { collection_slug: base };
  const opt = collections.find((c) => c.id === s.playlistId);
  if (opt?.editorsChoice && !opt.own) return { collection_slug: opt.id };
  return { collection_id: analyticsId(s.playlistId) };
}

/**
 * The Editor's Choice index, fetched at most once a page and only when a view needs it.
 * A failure resolves to null (and is retried next time): unknown is "not built-in".
 */
let ecIndex: Promise<EditorsChoiceIndex | null> | null = null;
export function loadEditorsChoice(
  fetchCollections: () => Promise<{ collections: ReadonlyArray<{ id: string; name: string; editorsChoice?: boolean }> }>,
): Promise<EditorsChoiceIndex | null> {
  if (!ecIndex) {
    ecIndex = fetchCollections()
      .then((r) => new Map(r.collections.filter((c) => c.editorsChoice).map((c) => [c.id, c.name] as const)))
      .catch(() => {
        ecIndex = null;
        return null;
      });
  }
  return ecIndex;
}
